import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, Clock, CheckCircle, ArrowRight, Scale } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NationalitySelect from "../components/NationalitySelect";
import { getNationalityCurrency } from "../data/nationalities";
import { useCurrency } from "../context/CurrencyContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComparePage() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(["", ""]);
  const [nationality, setNationality] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { convert, currency, setCurrency } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/countries`).then(r => setCountries(r.data));
  }, []);

  const addSlot = () => { if (selected.length < 4) setSelected([...selected, ""]); };
  const removeSlot = (i) => { if (selected.length > 2) setSelected(selected.filter((_, j) => j !== i)); };
  const setSlot = (i, v) => { const n = [...selected]; n[i] = v; setSelected(n); };

  const handleCompare = async () => {
    const codes = selected.filter(Boolean);
    if (codes.length < 2 || !nationality) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/compare`, { params: { destinations: codes.join(","), nationality, visa_type: "tourist" } });
      setResult(data);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Home</button>
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4 border border-blue-100"><Scale className="w-7 h-7 text-blue-600" /></div>
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Compare Visa Options</h1>
          <p className="text-sm text-slate-500">Compare pricing, processing times, and requirements side-by-side</p>
        </div>

        {/* Selection */}
        <div className="card-elevated p-6 mb-10">
          <div className="mb-4">
            <label className="text-xs text-slate-500 mb-1 block">Your Nationality</label>
            <div className="max-w-xs">
              <NationalitySelect value={nationality} onValueChange={(v) => { setNationality(v); setCurrency(getNationalityCurrency(v)); }} />
            </div>
          </div>
          <label className="text-xs text-slate-500 mb-2 block">Select Countries to Compare</label>
          <div className="flex flex-wrap gap-3 mb-4">
            {selected.map((code, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select value={code} onValueChange={v => setSlot(i, v)}>
                  <SelectTrigger data-testid={`compare-country-${i}`} className="w-[180px] h-11 border-slate-200 rounded-lg"><SelectValue placeholder={`Country ${i+1}`} /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 max-h-60">
                    {countries.map(c => <SelectItem key={c.country_code} value={c.country_code}><span className="flex items-center gap-2">{c.flag} {c.country_name}</span></SelectItem>)}
                  </SelectContent>
                </Select>
                {selected.length > 2 && <button onClick={() => removeSlot(i)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            {selected.length < 4 && (
              <button onClick={addSlot} className="h-11 px-4 border border-dashed border-slate-300 rounded-lg text-sm text-slate-400 hover:text-blue-600 hover:border-blue-300 flex items-center gap-1">
                <Plus className="w-4 h-4" /> Add
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={handleCompare} disabled={loading || selected.filter(Boolean).length < 2 || !nationality} data-testid="compare-btn"
              className="h-11 bg-blue-600 text-white font-semibold px-8 rounded-lg hover:bg-blue-700 disabled:opacity-40">
              {loading ? "Comparing..." : "Compare"}
            </button>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="w-[100px] h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {["USD", "INR", "GBP", "EUR", "AED", "CAD", "AUD"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Comparison Table */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="compare-results">
            <div className="overflow-x-auto">
              <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(${result.comparisons.length}, minmax(260px, 1fr))` }}>
                {result.comparisons.map((c) => {
                  const std = c.options.find(o => o.processing_type === "standard");
                  const exp = c.options.find(o => o.processing_type === "express");
                  return (
                    <div key={c.country_code} className="card-elevated rounded-2xl overflow-hidden" data-testid={`compare-card-${c.country_code}`}>
                      <div className="h-32 relative">
                        <img src={c.image} alt={c.country_name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-white/90 to-transparent" />
                        <div className="absolute bottom-3 left-4 flex items-center gap-2">
                          <span className="text-xl">{c.flag}</span><span className="text-sm font-bold text-slate-900">{c.country_name}</span>
                        </div>
                      </div>
                      <div className="p-5 space-y-4">
                        {/* Standard price */}
                        <div>
                          <span className="text-[10px] text-slate-400 uppercase font-semibold">Standard</span>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-slate-900 heading-font">{convert(std?.total || 0)}</span>
                            <span className="text-xs text-slate-400">{std?.days} days</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-emerald-600 mt-1"><CheckCircle className="w-3 h-3" />{std?.approval_rate}% approval</div>
                        </div>
                        {/* Express */}
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <span className="text-[10px] text-blue-600 uppercase font-semibold">Express</span>
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold text-slate-900">{convert(exp?.total || 0)}</span>
                            <span className="text-xs text-slate-400">{exp?.days} days</span>
                          </div>
                        </div>
                        {/* Fee breakdown */}
                        <div className="space-y-1 pt-2 border-t border-slate-100">
                          <div className="flex justify-between text-xs"><span className="text-slate-400">Gov. Fee</span><span className="text-slate-700">{convert(std?.government_fee || 0)}</span></div>
                          <div className="flex justify-between text-xs"><span className="text-slate-400">Service Fee</span><span className="text-slate-700">{convert(std?.service_fee || 0)}</span></div>
                        </div>
                        {/* Docs */}
                        <div>
                          <span className="text-[10px] text-slate-400 uppercase font-semibold">Documents</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.required_docs.slice(0, 3).map(d => <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{d}</span>)}
                            {c.required_docs.length > 3 && <span className="text-[10px] text-slate-400">+{c.required_docs.length - 3}</span>}
                          </div>
                        </div>
                        <button onClick={() => navigate(`/pricing?destination=${c.country_code}&nationality=${nationality}`)}
                          className="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 flex items-center justify-center gap-1">
                          Apply <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
